import { useEffect, useState } from 'react';
import { api, darf, datum, type Anmelder } from '../api.ts';
import { Fehlerkasten, ausFehler, type Fehlerlage } from './Formular.tsx';
import { Karte, Leer } from './Geruest.tsx';
import { useFrisch } from './Frisch.tsx';

/**
 * Die Glasfaser-Terminliste, die die Telekom bekommt.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „und generell will ich, dass du nun alles einmal fertig programmierst,
 *    schaue dir dazu die vorlagen an und baue alle punkte 1:1 NEU in dem
 *    neuen system."
 *
 * Früher war das eine ZWEITE Datei mit einem Reiter je Woche.
 *
 *   ORIGINALTEXT MARVIN (20.08.2026, mit zwei Bildschirmfotos der Reiterleiste)
 *     „warums tehen die wochen nicht in einer reihenfolge. das ist so total
 *      verwirrend. behebe das problem."
 *
 * Hier stellt sich die Frage nicht mehr: eine Woche ist eine Abfrage, keine
 * Registerkarte.
 *
 * NUR EINBLASEN, nur Montag bis Freitag, OHNE Uhrzeit. Die Liste nennt den
 * Tag, nicht die Stunde.
 */
export function Glasfasertermine({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [woche, setWoche] = useState<{ jahr: number; kw: number } | null>(null);
  const [ergebnis, setErgebnis] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const darfSenden = darf(ich, 'nachricht.senden');

  async function laden() {
    setD(await api.glasfaser(woche?.jahr, woche?.kw));
  }
  useEffect(() => { laden().catch((f) => setFehler(ausFehler(f))); }, [woche]);
  useFrisch(laden);
  if (!d) return null;

  function verschiebe(um: number) {
    // Über den Montag rechnen, nicht über die Wochennummer: sonst wird aus
    // KW 1 minus eins die KW 0, und die gibt es nicht.
    const mo = new Date(d.von + 'T12:00:00Z');
    mo.setUTCDate(mo.getUTCDate() + um * 7);
    const x = new Date(mo);
    x.setUTCDate(x.getUTCDate() + 4 - (x.getUTCDay() || 7));
    const jahr = x.getUTCFullYear();
    const kw = Math.ceil(
      (((x.getTime() - Date.UTC(jahr, 0, 1)) / 86400000) + 1) / 7);
    setWoche({ jahr, kw });
    setErgebnis(null);
  }

  async function senden(test: boolean) {
    setLaeuft(true); setFehler(null); setErgebnis(null);
    try {
      setErgebnis(await api.glasfaserSenden({
        jahr: d.woche.jahr, kw: d.woche.kw, test }));
      await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  const pdfWeg = `/api/glasfaser/termine.pdf`
    + `?jahr=${d.woche.jahr}&kw=${d.woche.kw}`;
  /* ZWEI KNÖPFE, ZWEI FORMATE (.217). Marvin: „will ich über 2 buttons dann
     selbst entscheiden, ob ich pdf oder excel erstellen lasse und
     herunterladen". Beide aus derselben Liste. */
  const excelWeg = `/api/glasfaser/termine.xlsx`
    + `?jahr=${d.woche.jahr}&kw=${d.woche.kw}`;

  return (
    <>
      <Fehlerkasten lage={fehler} />

      {ergebnis && (
        <div className={'hinweis ' + (ergebnis.gesendet ? '' : 'warnung')}>
          {ergebnis.gesendet ? (
            <>
              <b>Ist hinaus</b>, {ergebnis.termine} Termine an{' '}
              {ergebnis.an.join(', ')}
              {ergebnis.test && ' (Testversand)'}.
            </>
          ) : (
            <>
              <b>Nicht versendet.</b>{' '}
              {ergebnis.grund ?? 'Der Testmodus hält jede Mail zurück.'}
              {ergebnis.umgeleitet && ' Sie ging an die Testadresse.'}
            </>
          )}
          {ergebnis.hinweis && (
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              {ergebnis.hinweis}
            </div>
          )}
        </div>
      )}

      {!d.versand.aktiv && (
        <div className="hinweis warnung">
          <b>Der wöchentliche Versand ist abgeschaltet.</b>
          <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
            Die Liste lässt sich hier von Hand senden. Zum Scharfschalten:
            <code> GLASFASER_VERSAND_AKTIV</code> in der Konfiguration. Der
            Testmodus steht trotzdem darüber.
          </div>
        </div>
      )}

      <Karte
        titel={d.ueberschrift}
        rechts={
          <span className="reihe">
            <button className="knopf klein" onClick={() => verschiebe(-1)}>
              zurück
            </button>
            <button className="knopf klein" onClick={() => { setWoche(null); setErgebnis(null); }}>
              kommende
            </button>
            <button className="knopf klein" onClick={() => verschiebe(1)}>
              vor
            </button>
          </span>
        }
      >
        <div className="reihe" style={{ marginTop: 0, marginBottom: 10 }}>
          {/* Der Zeitraum steht schon in der Überschrift der Karte. Er stand
              hier ein zweites Mal, und zwar mit einem ANDEREN Ende: oben die
              ganze Kalenderwoche bis Sonntag, hier die Arbeitswoche bis
              Freitag. */}
          <span className="klein leise">
            {d.gesamt}{' '}
            {d.gesamt === 1 ? 'Einblastermin' : 'Einblastermine'}
          </span>
          <div className="wachsen" />
          <a className="knopf klein" href={pdfWeg} target="_blank"
             rel="noreferrer">
            PDF herunterladen
          </a>
          <a className="knopf klein" href={excelWeg} download>
            Excel herunterladen
          </a>
          {darfSenden && (
            <>
              <button className="knopf klein" disabled={laeuft}
                      onClick={() => senden(true)}>
                Testversand
              </button>
              <button className="knopf haupt" disabled={laeuft}
                      onClick={() => senden(false)}>
                {laeuft ? 'Einen Moment'
                  : `An die Telekom senden (${d.versand.format === 'pdf' ? 'PDF' : 'Excel'})`}
              </button>
            </>
          )}
        </div>

        <table className="liste">
          <thead>
            <tr>
              <th style={{ width: 150 }}>Tag</th>
              <th style={{ width: 70 }} className="zahl">Nr.</th>
              <th style={{ width: 150 }}>SM-Nummer</th>
              <th style={{ width: 130 }}>KLS-ID</th>
              <th>Adresse</th>
            </tr>
          </thead>
          <tbody>
            {d.tage.map((t: any) => (
              t.zeilen.length ? t.zeilen.map((z: any, i: number) => (
                <tr key={t.datum + z.nr} style={{ cursor: 'default' }}>
                  <td>
                    {i === 0 && (
                      <>
                        <b>{t.wochentag}</b>
                        <div className="klein leise">{t.datumText}</div>
                      </>
                    )}
                  </td>
                  <td className="zahl leise">{z.nr}</td>
                  <td className="klein">{z.sm}</td>
                  <td className="klein">{z.kls}</td>
                  <td>{z.adresse}</td>
                </tr>
              )) : (
                /* LEERE TAGE STEHEN DRIN. Ein Tag, der fehlt, sieht aus wie
                   ein vergessener Tag; ein leerer Tag ist eine Aussage. */
                <tr key={t.datum} style={{ cursor: 'default' }}>
                  <td>
                    <b>{t.wochentag}</b>
                    <div className="klein leise">{t.datumText}</div>
                  </td>
                  <td colSpan={4} className="klein leise">
                    - keine Termine -
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>

        <p className="klein leise" style={{ marginTop: 10 }}>
          Nur Einblastermine, Montag bis Freitag, ohne Uhrzeit: die Liste nennt
          den Tag. Ein stornierter Termin steht nicht darin.
          {d.versand.zuletzt && (
            <> Zuletzt echt versendet am {datum(d.versand.zuletzt)}.</>
          )}
        </p>
        {!d.versand.empfaenger && (
          <p className="klein rot">
            Es ist kein Empfänger hinterlegt. Ohne ihn weist der Versand jede
            Anfrage ab, statt ins Leere zu senden
            (<code>GLASFASER_VERSAND_EMPFAENGER</code>).
          </p>
        )}
      </Karte>
    </>
  );
}
